import {
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Paper,
  IconButton,
  Grid,
  Tooltip,
  SpeedDial,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
  ListAlt as ResultsIcon,
} from '@mui/icons-material';
import { GridRowParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import DataList from '../../components/DataList';
import htmlAppletService from '../../services/htmlAppletService';
import { translateApprovementStatus } from '../../utils/enumTranslaters';
import HtmlAppletResultsPanel from './HtmlAppletResultsPanel';

const HtmlAppletPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsApplet, setResultsApplet] = useState<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();

  const { checkPermission } = useCheckPermission();
  const canViewResults = checkPermission(['ViewHtmlAppletResults']);

  useEffect(() => {
    dispatch({ type: 'SHOW_QUERY' });
    htmlAppletService
      .list()
      .then((response) => {
        if (!response.hasError) setRows(response.records ?? []);
        else
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || 'Lista betöltése sikertelen', {
          variant: 'error',
        });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, []);

  const openResults = (row: any) => {
    setResultsApplet(row);
    setResultsOpen(true);
  };

  const getActions = (params: GridRowParams) => {
    var actions: any[] = [];

    let readonly = !checkPermission(['EditHtmlApplet']);
    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="Megtekintés">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Szerkesztés">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'Megtekintés' : 'Szerkesztés'}
        onClick={() => navigate(`/htmlApplets/edit/${params.row.id}`)}
      />
    );

    if (canViewResults) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Eredmények">
              <ResultsIcon />
            </Tooltip>
          }
          label="Eredmények"
          onClick={() => openResults(params.row)}
        />
      );
    }

    if (checkPermission(['DeleteHtmlApplet'])) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Törlés">
              <DeleteIcon />
            </Tooltip>
          }
          label="Törlés"
          onClick={() =>
            setParams({
              open: true,
              name: 'Biztosan törölni szeretné ezt az elemet?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: any[] = [
    { field: 'name', headerName: 'Név', flex: 150 },
    { field: 'version', headerName: 'Verzió', flex: 60 },
    {
      field: 'status',
      headerName: 'Státusz',
      flex: 100,
      valueFormatter: (value: any) => translateApprovementStatus(value),
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 100,
      align: 'right',
      getActions: getActions,
    } as any,
  ];

  const handleDelete = (id: string) => {
    htmlAppletService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar(response.errorMessages.join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Sikeres törlés!', {
          variant: 'success',
        });
        setRows(rows.filter((row) => row.id !== id));
      }
    });
  };

  return (
    <Paper>
      <Grid container sx={{
        p: 3
      }}>
        <Grid size={12}>
          <h2>HTML appletek</h2>
        </Grid>
        {checkPermission(['CreateHtmlApplet']) && (
          <Grid
            container
            size={12}
            sx={{
              p: 2,
              justifyContent: "end"
            }}>
            <Grid sx={{ display: { xs: 'none', sm: 'fixed' } }}>
              <Tooltip title="HTML applet létrehozása">
                <IconButton
                  component={RouterLink}
                  to={`/htmlApplets/create`}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <DataList
            rows={rows}
            columns={
              checkPermission(['ViewHtmlApplet'])
                ? columns
                : columns.filter((c) => c.field !== 'actions')
            }
          />
        </Grid>
        <ConfirmDeleteDialog />
        <Dialog
          open={resultsOpen}
          onClose={() => setResultsOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Eredmények
            {resultsApplet?.name ? `: ${resultsApplet.name}` : ''}
          </DialogTitle>
          <DialogContent dividers>
            {resultsApplet?.id && (
              <HtmlAppletResultsPanel
                appletId={resultsApplet.id}
                appletName={resultsApplet.name}
                showExport
                height={440}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResultsOpen(false)}>Bezárás</Button>
          </DialogActions>
        </Dialog>
        {checkPermission(['CreateHtmlApplet']) && (
          <SpeedDial
            onClick={() => {
              navigate(`/htmlApplets/create`);
            }}
            sx={{
              display: { xs: 'fixed', sm: 'none' },
              position: 'fixed',
              bottom: 16,
              right: 16,
            }}
            icon={<AddIcon />}
            ariaLabel={'Hozzáadás'}
          ></SpeedDial>
        )}
      </Grid>
    </Paper>
  );
};

export default HtmlAppletPage;
